import React from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import useAuth from '../../hooks/use-auth';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { ROUTES, getSubmitProjectPageTitle } from '../../routing';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import Navbar from '../../components/Navbar';
import { H1, H3, P } from '../../components/elements/Text';
import Button from '../../components/elements/Button';
import { MultiLineInput } from '../../components/elements/Input';
import { Helmet } from 'react-helmet-async';
import * as CrashReporter from '../../crash-reporter';
import { useMutation } from 'react-query';
import { useTeamsProfile } from '../../hooks/use-hydra';
import utc from 'dayjs/plugin/utc';
import Markdown from '../../components/Markdown';
import { postProjectSubmission } from '../../api/teams';

dayjs.extend(utc);
const questions = [
  {
    label: 'Your Company Project',
    question:
      "What is the company project that you'll work on? Describe it high-level.",
  },
  {
    label: 'Application',
    question:
      'What impact could your Company Project have on your role or company? If you can estimate any objective benefits (hours saved, capabilities unlocked, etc) feel free to share.',
  },
  {
    label: 'Extra Help',
    question:
      'Is there anything that would be helpful from your mentor or colleagues for you to get it done successfully?',
  },
];

const INTRO_TEXT_MARKDOWN = `
**The project is a practical application of your newly acquired skills**.

> 💡 The company project should demonstrate your ability to use your new skills within a current workflow or other use case at work right now.

---

### Where You Should Be Now

**What you will do for your Company Project should be taking shape at this point**. You should have clarity on the fundamental elements of your work, including your project's scope, any tooling or data requirements, and any necessary permissions to proceed.

> 💡 If you're still uncertain about your direction, now is the ideal time to reach out to your mentor or fellow cohort colleagues, as well as review the project examples page for inspiration!


### Assessment Focus
Remember that our evaluation isn't based on your project's results - you and your company and colleagues are the best judges of outcome value. Instead, we're interested in:

- **Practical Impact**: How your project delivers tangible benefits to your work
- **Process Documentation**: The methodical approach you took to reach your results
- **Feature Selection**: What components and data sets you chose to include
- **Challenge Navigation**: The obstacles you encountered and how you overcame them
- **Reflective Learning**: Your insights and how you might approach similar work differently in the future


### Presentation Options

Near the end of the cohort, the “Submit Company Project” action will unlock in your Cohort Milestones section on the home page. Submit as much as you can related to your project there.

**Format Freedom**: Choose the medium that best showcases your work - slides, documents, video demonstrations, or any other appropriate format. The key is effective communication of your process and outcomes.

### Next Steps

Please answer the questions below to let us know that you're all set with your Company Project, and/or how your mentor or colleagues can support you.
`;

export default function CheckIn2() {
  const { getTokenSilently } = useAuth();
  const {
    value: teamsProfile,
    loading: teamsProfileLoading,
    error: teamsProfileError,
    retry: retryTeamsProfile,
  } = useTeamsProfile();
  const history = useHistory();

  const latestCohort =
    teamsProfile?.onboardedCohorts.sort(
      (a, b) =>
        dayjs.utc(b.cohort.startDate).valueOf() -
        dayjs.utc(a.cohort.startDate).valueOf()
    )[0] ?? null;

  const [answers, setAnswers] = React.useState(
    [...Array(questions.length)].map(() => '')
  );
  const areAnswersFilledIn = answers.map((a) => a.length >= 3);

  const onQuestionInput = React.useCallback(
    (value: string, i: number) =>
      setAnswers(answers.map((answer, idx) => (i === idx ? value : answer))),
    [answers]
  );

  React.useEffect(() => {
    if (!latestCohort && !teamsProfileLoading) {
      toast.error(
        'You are not part of any cohort. Please reach out to us to resolve the issue.'
      );
      history.push(ROUTES.HOME.getPath());
    } else {
      // Prefill answers from latest CHECKIN_2 submission
      const submission = latestCohort?.milestones?.PROJECT_CHECKIN_2;
      if (submission && submission.survey) {
        const prefilled = questions.map(
          (_, i) => submission.survey[i]?.answer || ''
        );
        setAnswers(prefilled);
      }
    }
  }, [latestCohort, history, teamsProfileLoading]);

  const submitAnswers = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const formData = new FormData();
      formData.append(
        'survey',
        JSON.stringify(
          answers.map((answer, i) => ({
            answer,
            question: questions[i].question,
          }))
        )
      );
      formData.append('submissionType', 'CHECKIN_2');
      await postProjectSubmission(token, {
        formData,
        cohortId: latestCohort?.cohort.id.toString() ?? '',
        learnerId: teamsProfile?.learnerId?.toString() ?? '',
      });
    },
    onSuccess: async () => {
      await retryTeamsProfile();
      toast.success('Check-in #2 submitted successfully!');
      history.push(ROUTES.HOME.getPath());
    },
    onError: (err) => {
      CrashReporter.reportError(err as unknown as Error);
      toast.error(
        'Something went wrong. Please reach out to us to resolve the issue.'
      );
    },
  });

  // Detect unsaved changes
  const hasUnsavedChanges = React.useMemo(() => {
    if (submitAnswers.isLoading) return false;
    const submission = latestCohort?.milestones?.PROJECT_CHECKIN_2;
    if (!submission) {
      return false;
    }
    const originalAnswers = submission?.survey?.length
      ? questions.map((_, i) => submission.survey[i]?.answer || '')
      : [...Array(questions.length)].map(() => '');
    return answers.some((a, i) => a !== originalAnswers[i]);
  }, [answers, latestCohort, submitAnswers.isLoading]);

  // beforeunload handler
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const isSubmitted =
    !!latestCohort?.milestones?.PROJECT_CHECKIN_2?.submittedAt;

  return (
    <PageWithAsyncData
      className={''}
      isLoading={teamsProfileLoading}
      error={teamsProfileError}
      retry={retryTeamsProfile}
    >
      <>
        <Helmet>
          <title>{getSubmitProjectPageTitle()}</title>
        </Helmet>
        <Navbar
          breadCrumbs={[
            {
              label: 'Home',
              url: ROUTES.HOME.getPath(),
            },
            {
              label: 'Company Project Check-in #1',
              url: null,
            },
          ]}
        />
        <PageWrapper
          style={{
            margin: '0 auto 52px',
            maxWidth: 820,
          }}
        >
          <H1>Project Check-in #2</H1>
          <Markdown md={INTRO_TEXT_MARKDOWN} />
          {questions.map(({ label, question }, i) => (
            <>
              <H3>{label}</H3>
              <P>{question}</P>
              <MultiLineInput
                minRows={3}
                placeholder={'Answer here...'}
                value={answers[i]}
                onChange={(e) => onQuestionInput(e.target.value, i)}
              />
            </>
          ))}
          <div style={{ marginTop: '52px' }}>
            <Button
              onClick={() => submitAnswers.mutateAsync()}
              isDisabled={!areAnswersFilledIn.every(Boolean)}
              label={
                isSubmitted ? 'Update Check-in #2' : 'Complete Check-in #2'
              }
              type="primary"
              isLoading={submitAnswers.isLoading}
            />
          </div>
          <Prompt
            when={hasUnsavedChanges}
            message="You have unsaved changes. Are you sure you want to leave this page?"
          />
        </PageWrapper>
      </>
    </PageWithAsyncData>
  );
}
